import {
  Box,
  Checkbox,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";

import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { visuallyHidden } from "@mui/utils";
import { Order } from "features/common/Interfaces";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "rgb(255 255 255)",
    color: "rgb(62 77 91)",
    padding: "16px 0",
    fontSize: 14,
    "@media (max-width:1024px)": {
      fontSize: "12px",
    },
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));
const customStyles = {
  checkBox: {
    width: "16px",
    height: "16px",
  },
};

interface ITHeadProps<T> {
  data: T[];
  onRequestSort: (event: React.MouseEvent<unknown>, property: T) => void;
  order: Order;
  orderBy: string;
  actionLabels: string[];
  counters: { [key in T as string]: number };
  withCheckBox?: boolean;
  selectAll?: boolean;
  setSelectAll?(): void;
}

function THead<T>({
  data,
  order,
  orderBy,
  onRequestSort,
  actionLabels,
  counters,
  withCheckBox = false,
  selectAll = false,
  setSelectAll,
}: Readonly<ITHeadProps<T>>) {
  const { t, i18n } = useTranslation();

  const createSortHandler =
    (property: T) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };
  const align = i18n.dir() === "rtl" ? "right" : "left";
  const handleToggleAll = () => setSelectAll?.();
  const checkAllBox = (checked: boolean) => (
    <Checkbox
      key="checkAllBox"
      onChange={handleToggleAll}
      checked={checked}
      color="primary"
      icon={<CheckBoxOutlineBlankIcon sx={customStyles.checkBox} />}
      checkedIcon={<CheckBoxIcon sx={customStyles.checkBox} />}
    />
  );
  return (
    <TableHead>
      <TableRow>
        {withCheckBox ? (
          <StyledTableCell
            key="CheckAll"
            align="center"
            sx={{ padding: 0, width: 0 }}
          >
            {checkAllBox(selectAll)}
          </StyledTableCell>
        ) : null}
        {data.map((col) => (
          <StyledTableCell key={`${col}`} align={align}>
            <TableSortLabel
              active={orderBy === `${col}`}
              direction={orderBy === `${col}` ? order : "asc"}
              onClick={createSortHandler(col)}
              sx={{ direction: "rtl" }}
            >
              {t(`${col}`)}
              {counters[`${col}`] ? " (" + counters[`${col}`] + ") " : null}
              {orderBy === `${col}` ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </StyledTableCell>
        ))}
        {actionLabels.map((col) => (
          <StyledTableCell
            key={col}
            align="center"
            sx={{ width: "30px", userSelect: "none" }}
          >
            {t(col)}
          </StyledTableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export default THead;
